import { CheckOutlined, CloseOutlined, PlusCircleOutlined, SelectOutlined } from "@ant-design/icons";
import { Button, Col, Flex, Form, Radio, RadioChangeEvent, Row, Select, Space, Table, Tooltip } from "antd";
import Item from "antd/es/descriptions/Item";
import React, { useEffect } from "react";
import { useState } from "react";
import { NumericFormat } from "react-number-format";
import { AplicarRateioContaFinanceiraModel } from "../../../../models/contaFinanceira/aplicarRateioContaFinanceiraModel";
import { GerarCalculoRateioModel } from "../../../../models/contaFinanceira/gerarCalculoRateioModel";
import { RateioJsonModel } from "../../../../models/contaFinanceira/rateioJsonModel";
import { aplicarRateioContaFinanceira, gerarCalculoRateio, listarDetalheContaFinanceira } from "../../../../services/contaFinanceiraService";
import { currencyBRL, realParaNumber } from "../../../../util/dinheiroBRAuxiliar";
import { formatPorcentagem } from "../../../../util/formats";
import { useAppDispatch, useAppSelector } from "../../../../util/hooks";
import { setModalRateioContaFinanceira } from "../../../../redux/slices/contaFinanceira/contaFinanceiraSlice";
import { buscarCentroCusto } from "../../../../services/centroCustoService";
import SelectTree from "../../../../components/inputs/selectTree";
import { findNodeById } from "../../../../util/functions/findNodeById";
import { toastWarning } from "../../../../util/toast";
import { buscarPlanoContaFinanceiro } from "../../../../services/planoContaFinanceiroService";
import * as PhosphorIcon from "phosphor-react";
import { buscarRateioCentroCusto } from "../../../../services/rateioCentroCustoService";

interface Item {
  key: string;
  idPlanoContaFinanceiro: number | null;
  idCentroCusto: number | null;
  valor: number | null;
}

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: "number" | "selectConta" | "selectCentro";
  record: Item;
  index: number;
  children: React.ReactNode;
}

const Rateio = (props: any) => {
  //estados globais
  const centroCustoState = useAppSelector((state) => state.centroCusto);
  const contaFinanceiraState = useAppSelector((state) => state.contaFinanceira);
  const planoContaFinanceiroState = useAppSelector((state) => state.planoContaFinanceiro);
  const rateioCentroCustoState = useAppSelector((state) => state.rateioCentroCusto);

  //hooks
  const dispatch = useAppDispatch();

  //estados locais
  const [formManual] = Form.useForm();
  const [formRateio] = Form.useForm();
  const [valueRadio, setValueRadio] = useState("Manual");
  const [editingKey, setEditingKey] = useState("");
  const [count, setCount] = useState(2);
  const [totalItens, setTotalItens] = useState<number>(0);
  const [dataSource, setData] = useState<any[]>([]);

  const fetchData = async () => {
    const retorno = await dispatch(listarDetalheContaFinanceira(props.conta.id));
    setCount(retorno.payload.length);
    var initialData: Item[] = [];
    for (let i = 0; i < retorno.payload.length; i++) {
      initialData.push({
        key: i.toString(),
        idPlanoContaFinanceiro: retorno.payload[i].idPlanoContaFinanceiro,
        idCentroCusto: retorno.payload[i].idCentroCusto,
        valor: retorno.payload[i].valor,
      });
    }
    let soma = retorno.payload.reduce((prev: any, current: any) => {
      return prev + +(current.valor === null ? 0 : realParaNumber(current.valor.toString()));
    }, 0);
    setTotalItens(soma);
    setData(initialData);
  };

  useEffect(() => {
    fetchData();
    if (centroCustoState.list.length === 0) dispatch(buscarCentroCusto(centroCustoState.busca));
    if (planoContaFinanceiroState.list.length === 0) dispatch(buscarPlanoContaFinanceiro(planoContaFinanceiroState.busca));
    if (rateioCentroCustoState.list.length === 0) dispatch(buscarRateioCentroCusto(rateioCentroCustoState.busca));
  }, []);

  const radioOptions = [
    { label: "Manual", value: "Manual" },
    { label: "Rateio", value: "Rateio" },
  ];

  //início funções tabela centro de custo
  const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({ editing, dataIndex, title, inputType, record, index, children, ...restProps }) => {
    const inputNode =
      inputType === "number" ? (
        <NumericFormat
          className="ant-input noBoderColor number-input"
          thousandSeparator="."
          decimalSeparator=","
          decimalScale={2}
          prefix="R$"
          fixedDecimalScale={true}
          onValueChange={(e) => {
            formManual.setFieldValue("valor", e.floatValue);
          }}
        />
      ) : inputType === "selectConta" ? (
        <SelectTree loading={planoContaFinanceiroState.loading} list={planoContaFinanceiroState.list} />
      ) : (
        <SelectTree loading={centroCustoState.loading} list={centroCustoState.list} />
      );

    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={[
              {
                required: true,
                message: `Informe o ${title}`,
              },
            ]}
          >
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  const isEditing = (record: Item) => record.key === editingKey;

  const edit = (record: Partial<Item> & { key: React.Key }) => {
    formManual.setFieldsValue({
      idPlanoContaFinanceiro: record.idPlanoContaFinanceiro,
      idCentroCusto: record.idCentroCusto,
      valor: record.valor,
    });
    setEditingKey(record.key);
  };

  const handleDelete = (key: React.Key) => {
    const newData = dataSource.filter((item: any) => item.key !== key);
    let soma = newData.reduce((prev, current) => {
      return prev + +(current.valor === null ? 0 : realParaNumber(current.valor.toString()));
    }, 0);
    setTotalItens(soma);
    setData(newData);
  };

  const cancel = (key: React.Key) => {
    const hasData = dataSource.find((item: any) => item.key === key);
    if (hasData?.idPlanoContaFinanceiro === null) {
      handleDelete(key);
    }
    setEditingKey("");
  };
  const handleAdd = () => {
    if (editingKey === "") {
      const newData: Item = {
        key: count.toString(),
        idPlanoContaFinanceiro: null,
        idCentroCusto: null,
        valor: null,
      };
      setData([...dataSource, newData]);
      setEditingKey(count.toString());
      setCount(count + 1);
      formManual.resetFields();
    } else {
      toastWarning("Para adicionar uma nova classificação, salve primeiro a classificação que já está em edição.");
    }
  };

  const save = async (key: React.Key, abrir: boolean) => {
    try {
      const row = (await formManual.validateFields()) as Item;
      const newData = [...dataSource];
      const index = newData.findIndex((item) => key === item.key);
      if (index > -1) {
        const item = newData[index];
        newData.splice(index, 1, {
          ...item,
          ...row,
        });
        setData(newData);
        let soma = newData.reduce((prev, current) => {
          return prev + +(current.valor === null ? 0 : realParaNumber(current.valor.toString()));
        }, 0);
        setTotalItens(soma);
        setEditingKey("");
        if (abrir) {
          const newData: Item = {
            key: count.toString(),
            idPlanoContaFinanceiro: null,
            idCentroCusto: null,
            valor: null,
          };
          setData([...dataSource, newData]);
          setEditingKey(count.toString());
          setCount(count + 1);
          formManual.resetFields();
        }
      } else {
        newData.push(row);
        setData(newData);
        setEditingKey("");
        if (abrir) {
          const newData: Item = {
            key: count.toString(),
            idPlanoContaFinanceiro: null,
            idCentroCusto: null,
            valor: null,
          };
          setData([...dataSource, newData]);
          setEditingKey(count.toString());
          setCount(count + 1);
          formManual.resetFields();
        }
      }
    } catch (e) {}
  };

  const columns = [
    {
      title: "PLANO DE CONTA FINANCEIRO",
      dataIndex: "idPlanoContaFinanceiro",
      editable: true,
      render: (_: any, record: any) => findNodeById(planoContaFinanceiroState.list, record.idPlanoContaFinanceiro)?.denominacao,
    },
    {
      title: "CENTRO DE CUSTO",
      dataIndex: "idCentroCusto",
      editable: true,
      render: (_: any, record: any) => findNodeById(centroCustoState.list, record.idCentroCusto)?.denominacao,
    },
    {
      title: "VALOR PRINCIPAL",
      dataIndex: "valor",
      editable: true,
      align: "right" as "right",
      render: (_: any, { valor }: any) => currencyBRL(valor === null ? 0 : valor),
    },
    {
      title: "AÇÕES",
      dataIndex: "acao",
      render: (_: any, record: Item) => {
        const editable = isEditing(record);
        return editable ? (
          <Flex justify="center" align="center" gap={4}>
            <Tooltip title="Salvar">
              <PhosphorIcon.CheckCircle style={{ cursor: "pointer" }} size={20} color="#334155" onClick={() => save(record.key, false)} />
            </Tooltip>
            <Tooltip title="Cancelar">
              <PhosphorIcon.XCircle style={{ cursor: "pointer" }} size={20} color="#334155" onClick={() => cancel(record.key)} />
            </Tooltip>
          </Flex>
        ) : (
          <Flex justify="center" gap={4}>
            <Tooltip title="Editar">
              <PhosphorIcon.Pencil style={{ cursor: "pointer" }} size={20} color="#334155" onClick={() => edit(record)} />
            </Tooltip>
            <Tooltip title="Remover">
              <PhosphorIcon.Trash style={{ cursor: "pointer" }} size={20} color="#334155" onClick={() => handleDelete(record.key)} />
            </Tooltip>
          </Flex>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: Item) => ({
        record,
        inputType: col.dataIndex === "valor" ? "number" : col.dataIndex === "idCentroCusto" ? "selectCentro" : "selectConta",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  const columnsRateio = [
    {
      title: "DISTRIBUIÇÃO",
      dataIndex: "porcentagem",
      render: (porcentagem: number) => formatPorcentagem(porcentagem),
    },
    {
      title: "CENTRO DE CUSTO",
      dataIndex: "idCentroCusto",
      render: (_: any, record: any) => findNodeById(centroCustoState.list, record.idCentroCusto)?.denominacao,
    },
    {
      title: "VALOR",
      dataIndex: "valorRateado",
      align: "right" as "right",
      render: (_: any, { valorRateado }: any) => currencyBRL(valorRateado),
    },
  ];

  const onChangeRadio = ({ target: { value } }: RadioChangeEvent) => {
    setValueRadio(value);
  };

  const validaCentroCusto = (value: RateioJsonModel[]) => {
    var erro: boolean = false;
    if (value.length === 0) {
      erro = true;
      toastWarning("Informar pelo menos uma classificação de contas a pagar");
    } else {
      for (var i = 0; i < value.length; i++) {
        if (value[i].valor === null || value[i].idCentroCusto === null || value[i].idPlanoContaFinanceiro === null) {
          toastWarning("Pelo menos uma das classificações de contas a pagar está incorreta");
          erro = true;
        } else {
          erro = false;
        }
      }
    }
    return erro;
  };

  const gerarRateio = () => {
    const gerar: GerarCalculoRateioModel = {
      valor: props.conta.valor,
      idRateio: formRateio.getFieldValue("idRateio"),
    };
    dispatch(gerarCalculoRateio(gerar));
  };

  const submit = () => {
    let submit: AplicarRateioContaFinanceiraModel = {} as AplicarRateioContaFinanceiraModel;
    let dataChange: RateioJsonModel[] = [];

    submit.idContaFinanceira = props.conta.id;

    if (valueRadio === "Manual") {
      dataSource.forEach((v: any) => {
        var detalhes = {
          idCentroCusto: v.idCentroCusto,
          idPlanoContaFinanceiro: v.idPlanoContaFinanceiro,
          valor: realParaNumber(v.valor),
        };
        dataChange.push(detalhes);
      });
    } else {
      contaFinanceiraState.listCalculo.forEach((v: any) => {
        var detalhes = {
          idCentroCusto: v.idCentroCusto,
          idPlanoContaFinanceiro: formRateio.getFieldValue("idPlanoContaFinanceiro"),
          valor: v.valorRateado,
        };
        dataChange.push(detalhes);
      });
    }

    const valida = validaCentroCusto(dataChange);

    if (!valida) {
      if (props.conta.valor !== totalItens) {
        toastWarning("O valor total da classificação da conta difere do valor principal");
      } else {
        submit.detalhes = dataChange;
        dispatch(aplicarRateioContaFinanceira({ value: submit, busca: contaFinanceiraState.busca, buscarCentroCusto: props.conta.id }));
      }
    }
  };

  const voltar = () => {
    dispatch(setModalRateioContaFinanceira(false));
  };

  return (
    <Flex gap={12} vertical>
      <Row gutter={12} align="bottom">
        <Col span={24}>
          <Flex justify="end">
            <Radio.Group options={radioOptions} onChange={onChangeRadio} value={valueRadio} optionType="button" buttonStyle="solid" />
          </Flex>
        </Col>
      </Row>
      {valueRadio === "Rateio" ? (
        <Flex gap={12} vertical>
          <Row gutter={12} className="rateioHeader">
            SELECIONAR RATEIO
          </Row>
          <Flex className="rateioDentro" vertical>
            <Form form={formRateio} layout="vertical">
              <Row gutter={12} className="rateioForm">
                <Col span={10}>
                  <Form.Item label="Conta financeira" name="idPlanoContaFinanceiro">
                    <SelectTree loading={planoContaFinanceiroState.loading} list={planoContaFinanceiroState.list} />
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Form.Item label="Rateio" name="idRateio">
                    <Select
                      showSearch
                      optionFilterProp="label"
                      loading={rateioCentroCustoState.loading}
                      onChange={(e) => formRateio.setFieldValue("idRateio", e)}
                      options={rateioCentroCustoState.list.map((a: any) => ({
                        value: a.id,
                        label: a.denominacao,
                        disabled: !a.ativo,
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={4} style={{ marginTop: 31 }}>
                  <Button className="buttonPrimary" onClick={gerarRateio} icon={<SelectOutlined />}>
                    GERAR
                  </Button>
                </Col>
              </Row>
            </Form>
            <Row gutter={12}>
              <Col span={24}>
                <Table size="small" loading={contaFinanceiraState.loadingCalculo} dataSource={contaFinanceiraState.listCalculo} columns={columnsRateio} pagination={false} />
              </Col>
            </Row>
          </Flex>
        </Flex>
      ) : (
        <Flex gap={12} vertical>
          <Form layout="vertical">
            <Row gutter={12}>
              <Col span={4}>
                <Flex gap={12}>
                  <Form.Item label=" ">
                    <Button onClick={handleAdd} type="primary" icon={<PlusCircleOutlined />}>
                      Adicionar
                    </Button>
                  </Form.Item>
                </Flex>
              </Col>
              <Col span={20}>
                <Flex justify="end">
                  <Form.Item label="Total dos itens">
                    <NumericFormat
                      className="ant-input noBoderColor number-input"
                      thousandSeparator="."
                      decimalSeparator=","
                      decimalScale={2}
                      prefix="R$"
                      fixedDecimalScale={true}
                      disabled
                      value={totalItens}
                    />
                  </Form.Item>
                </Flex>
              </Col>
            </Row>
          </Form>
          <Form form={formManual} component={false}>
            <Row gutter={12}>
              <Col span={24}>
                <Form.Item>
                  <Table
                    size="small"
                    components={{
                      body: {
                        cell: EditableCell,
                      },
                    }}
                    rowClassName="editable-row"
                    dataSource={dataSource}
                    columns={mergedColumns}
                    pagination={false}
                    loading={contaFinanceiraState.loading}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Flex>
      )}
      <div className="modalFooter">
        <Space size="middle">
          <Button className="cancelButton" onClick={voltar} icon={<CloseOutlined />}>
            FECHAR
          </Button>
          <Button onClick={submit} className="buttonPrimary" icon={<CheckOutlined />} disabled={editingKey === "" ? false : true}>
            SALVAR
          </Button>
        </Space>
      </div>
    </Flex>
  );
};

export default Rateio;
